import {Link as RoutedLink} from 'react-router-dom';

import classNames from 'classnames';

import {Avatar} from '../avatar/avatar';
import {colors} from '../colors/colors';
import {MedalRegularIcon} from '../icon/svg/medal-regular-icon';
import {Stack} from '../layout/stack/stack';
import {Link} from '../link/link';
import {LinkedEntity} from '../linked-entity/linked-entity';
import {Body} from '../typography/body';

import type {EntityLeaderboardRowData, OtherLeaderboardRowData} from './types';

import styles from './leaderboard.module.css';

interface Props {
    data: EntityLeaderboardRowData | OtherLeaderboardRowData;
    position: number;
    hideMedal?: boolean;
}

export function LeaderboardRow(props: Props) {
    const Icon = props.data.rowType === 'entity' ? null : props.data.icon;

    const rowClassNames = classNames(styles['leaderboard-row'], {
        [styles['leaderboard-row--clickable']]:
            props.data.rowType === 'other' && props.data.onClick,
    });

    return (
        <div
            className={rowClassNames}
            onClick={props.data.rowType === 'other' ? props.data.onClick : undefined}
        >
            <Stack isHorizontal={true} spacing={4} alignItems='center'>
                <div className='flex-grow'>
                    <Stack isHorizontal={true} spacing={8} alignItems='center'>
                        <Body size='small'>{props.position}</Body>
                        {props.data.rowType === 'entity' ? (
                            <>
                                <Avatar
                                    avatarUrl={props.data.avatarUrl}
                                    initials={props.data.initials}
                                    type={props.data.entityType}
                                    size={24}
                                />
                                <LinkedEntity
                                    type={props.data.entityType}
                                    name={props.data.label}
                                    htmlUrl={props.data.htmlUrl}
                                />
                            </>
                        ) : (
                            <>
                                {Icon && (
                                    <Icon
                                        size={20}
                                        fill={
                                            props.data.iconColor
                                                ? colors[props.data.iconColor]
                                                : undefined
                                        }
                                        color={
                                            props.data.iconColor
                                                ? colors[props.data.iconColor]
                                                : undefined
                                        }
                                    />
                                )}
                                {props.data.renderLabel ? (
                                    props.data.renderLabel()
                                ) : props.data.pathName ? (
                                    // @ts-expect-error
                                    <Link as={RoutedLink} to={props.data.pathName}>
                                        {props.data.label}
                                    </Link>
                                ) : (
                                    <Body size='small' color='dark'>
                                        {props.data.label}
                                    </Body>
                                )}
                            </>
                        )}
                    </Stack>
                </div>
                {props.data.secondaryValue && (
                    <div
                        className={styles['leaderboard-secondary-value']}
                        data-tip={props.data.secondaryValueTooltip}
                        data-place='top'
                        data-effect='solid'
                        data-class={styles['tooltip']}
                    >
                        <Body size='small' color='light'>
                            {props.data.secondaryValue}
                        </Body>
                    </div>
                )}
                <div className={styles['leaderboard-value']}>
                    <Stack isHorizontal={true} spacing={8} alignItems='center' justifyContent='end'>
                        {props.position === 1 && !props.hideMedal && (
                            // @ts-expect-error
                            <MedalRegularIcon size={20} fill={colors.greenDk} wrapWithDiv={false} />
                        )}
                        <Body size='small'>{props.data.value}</Body>
                    </Stack>
                </div>
            </Stack>
        </div>
    );
}
